import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

export type AddonCategory = 'brand' | 'thematic' | 'free';

export interface LibraryAddon {
  id: string;
  slug: string;
  name: string;
  short_description: string | null;
  long_description: string | null;
  brand: string | null;
  category: AddonCategory;
  price_eur: number;
  yearly_update_price_eur: number | null;
  lemonsqueezy_variant_id: string | null;
  starter_category_filter: string | null;
  preview_count_materials: number;
  preview_count_stratigraphies: number;
  is_active: boolean;
  sort_order: number;
  includes_addon_slugs: string[] | null;
  version_year: number | null;
  brand_slug: string | null;
  is_current_year: boolean;
  upgrade_price_eur: number | null;
  release_family_slug: string | null;
}

export interface OrgEntitlement {
  id: string;
  organization_id: string;
  addon_id: string;
  purchased_at: string;
  expires_at: string | null;
}

export function useLibraryAddons() {
  // Fetch ALL addons (anche is_active=false) per permettere a Store di calcolare
  // "bundle ha contenuto pendente" guardando le entries inactive incluse nel bundle.
  // Il filtro is_active=true viene applicato lato UI a livello di card visibilita'.
  return useQuery<LibraryAddon[]>({
    queryKey: ['library_addons'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('library_addons')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as LibraryAddon[];
    },
  });
}

export function useOrgEntitlements() {
  const { currentOrganizationId } = useCurrentOrganization();
  return useQuery<OrgEntitlement[]>({
    queryKey: ['org_entitlements', currentOrganizationId],
    enabled: !!currentOrganizationId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!currentOrganizationId) return [];
      const { data, error } = await supabase
        .from('org_library_entitlements')
        .select('*')
        .eq('organization_id', currentOrganizationId);
      if (error) throw error;
      return (data ?? []) as OrgEntitlement[];
    },
  });
}

export function useAddonsWithEntitlements() {
  const addons = useLibraryAddons();
  const entitlements = useOrgEntitlements();

  const ownedAddonIds = new Set((entitlements.data ?? []).map((e) => e.addon_id));

  // F27 upgrade pricing: per ogni addon, capisce se l'org ha già una versione precedente
  // della STESSA famiglia (stesso brand_slug + category). Se sì, expose il prezzo upgrade.
  const allAddonsById = new Map((addons.data ?? []).map((a) => [a.id, a]));
  const ownedAddons = (entitlements.data ?? [])
    .map((e) => allAddonsById.get(e.addon_id))
    .filter(Boolean) as LibraryAddon[];

  const enrichedAddons = (addons.data ?? []).map((a) => {
    const owned = ownedAddonIds.has(a.id);
    // Possiedi una versione precedente della stessa famiglia?
    const hasPreviousVersion =
      !owned &&
      a.brand_slug != null &&
      a.version_year != null &&
      ownedAddons.some((own) =>
        own.brand_slug === a.brand_slug &&
        own.category === a.category &&
        own.version_year != null &&
        own.version_year < (a.version_year ?? 0),
      );
    return {
      ...a,
      owned,
      hasPreviousVersion,
      // Prezzo da mostrare nello Store: upgrade se hasPreviousVersion + upgrade_price_eur valorizzato
      effectivePrice: hasPreviousVersion && a.upgrade_price_eur != null
        ? Number(a.upgrade_price_eur)
        : Number(a.price_eur),
    };
  });

  return {
    isLoading: addons.isLoading || entitlements.isLoading,
    addons: enrichedAddons,
    entitlements: entitlements.data ?? [],
  };
}

/**
 * F29.3 — Raggruppa addon per release_family_slug.
 * Lo Store mostra UNA card per famiglia (es. "Stratigrafie Fassa Bortolo")
 * con dropdown anno. Il cliente sceglie quale versione vuole acquistare.
 */
export interface AddonFamily {
  family_slug: string;
  brand_slug: string | null;
  category: AddonCategory;
  // Le versioni disponibili (ordinate dalla più recente)
  versions: (LibraryAddon & { owned: boolean; hasPreviousVersion: boolean; effectivePrice: number })[];
  // La versione "default" che si mostra: corrente, oppure quella posseduta, o l'ultima
  defaultVersion: (LibraryAddon & { owned: boolean; hasPreviousVersion: boolean; effectivePrice: number });
  // Almeno una versione e' posseduta?
  anyOwned: boolean;
}

export function useAddonFamilies() {
  const { addons, entitlements, isLoading } = useAddonsWithEntitlements();

  // Group per family_slug
  const map = new Map<string, AddonFamily>();
  for (const a of addons) {
    if (!a.is_active) continue;
    const fam = a.release_family_slug ?? a.slug; // fallback su slug se mancante
    let entry = map.get(fam);
    if (!entry) {
      entry = {
        family_slug: fam,
        brand_slug: a.brand_slug,
        category: a.category,
        versions: [],
        defaultVersion: a,
        anyOwned: false,
      };
      map.set(fam, entry);
    }
    entry.versions.push(a);
    if (a.owned) entry.anyOwned = true;
  }

  // Per ogni famiglia: ordina versions per anno desc + scegli default
  const families = Array.from(map.values()).map((f) => {
    f.versions.sort((a, b) => (b.version_year ?? 0) - (a.version_year ?? 0));
    // Default: la versione corrente (is_current_year=true), o l'ultima
    f.defaultVersion = f.versions.find((v) => v.is_current_year) ?? f.versions[0];
    return f;
  });

  return { isLoading, families, entitlements };
}
